import { EmploymentStatus, type Occupation } from '@egw/api/src/models/enrollment/enrollment'
import { Button, Field, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { type FC, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { employmentStatusItems, occupationItems } from '../../../constants/confirm-personal-info/confirm-personal-info'

// Compononent to Be used within a FormProvider from react-hook-form
export const EditEmploymentOccupation: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { openModal, closeModal, isOpen } = useModalState()
  const [defaultEmploymentStatusField, setDefaultEmploymentStatusField] = useState<EmploymentStatus | undefined>(
    undefined,
  )
  const [defaultOccupationField, setDefaultOccupationField] = useState<Occupation | undefined>(undefined)

  const { control, setValue, watch } = useFormContext()

  const employmentStatusField = watch('employmentStatus')
  const occupationField = watch('occupation')

  const isEmployedOrIndependent =
    (employmentStatusField as EmploymentStatus) === EmploymentStatus.Employed ||
    (employmentStatusField as EmploymentStatus) === EmploymentStatus.Independent

  useEffect(() => {
    if (employmentStatusField !== undefined && !defaultOccupationField) {
      setDefaultEmploymentStatusField(employmentStatusField)
    }
    if (occupationField !== undefined && !defaultOccupationField) {
      setDefaultOccupationField(occupationField)
    }
  }, [employmentStatusField, occupationField])

  // Closes the modal and reverts the value to the default value
  // Only using the closeModal method will persist the value
  const closeWithoutSaving = () => {
    setValue('employmentStatus', defaultEmploymentStatusField)
    setValue('occupation', defaultOccupationField)
    closeModal()
  }

  const closeWithSaving = () => {
    setDefaultEmploymentStatusField(employmentStatusField)
    setDefaultOccupationField(occupationField)
    closeModal()
  }

  // Disable the CTA if the employment status is not set or when the occupation is available but not set
  const disableCTA = !(isEmployedOrIndependent ? employmentStatusField && occupationField : employmentStatusField)

  return (
    <>
      <Field
        className="border-b py-sm"
        valueClassName="text-text-meta typo-primary-base-regular"
        value={t('edit_fields.employment_status_title')}
        rightValue={t(`employment_status_options.${employmentStatusField}`)}
        testId="editEmploymentStatus"
        onEdit={openModal}
      />
      {isEmployedOrIndependent && occupationField ? (
        <Field
          className="border-b py-sm"
          valueClassName="text-text-meta typo-primary-base-regular"
          value={t('edit_fields.occupation_title')}
          rightValue={occupationField ? t(`occupation_options.${occupationField}`) : null}
          testId="editOccupation"
          onEdit={openModal}
        />
      ) : null}

      <ModalDrawer isOpen={isOpen} onClose={closeWithoutSaving} title={t('edit_fields.edit_employment_status')}>
        <>
          <p className="typo-primary-base-semibold mb-md">{t('edit_fields.employment_status_title')}</p>

          <Controller
            name="employmentStatus"
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { ref, ...restField } }) => (
              <RadioGroup
                {...restField}
                onChange={(e) => {
                  restField.onChange(e)
                  if (
                    [EmploymentStatus.Unemployed, EmploymentStatus.Retired, EmploymentStatus.Student].includes(
                      e as EmploymentStatus,
                    )
                  ) {
                    setValue('occupation', '')
                  }
                }}
                options={employmentStatusItems.map((item) => ({
                  value: item.value,
                  label: t(`employment_status_options.${item.value}`),
                }))}
                name="employmentStatus"
                className="pb-xl typo-primary-base"
                testId="employment-status-radio"
              />
            )}
          />

          {isEmployedOrIndependent ? (
            <>
              <p className="typo-primary-base-semibold mb-md">{t('edit_fields.occupation_title')}</p>
              <Controller
                name="occupation"
                control={control}
                rules={{
                  required: isEmployedOrIndependent,
                }}
                render={({ field: { ref, ...restField } }) => (
                  <RadioGroup
                    {...restField}
                    onChange={(e) => {
                      restField.onChange(e)
                    }}
                    options={occupationItems.map((item) => ({
                      value: item.value,
                      label: t(`occupation_options.${item.value}`),
                    }))}
                    name="occupation"
                    className="pb-sm"
                    testId="occupation-radio"
                  />
                )}
              />
            </>
          ) : null}
          <Button
            isDisabled={disableCTA}
            size="small"
            onClick={closeWithSaving}
            className="w-full"
            testId="primaryAction_button"
          >
            {t('common.confirm')}
          </Button>
        </>
      </ModalDrawer>
    </>
  )
}
